import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Visibility } from "@material-ui/icons";
import { connect } from "react-redux";

import toJs from "../../../hoc/toJS";
import select from "../../../utils/select";

import KPTable from "../../../components/KPTable";
import ScientificResearchViewModal from "../../Report/ResearchRewardTable/ScientificResearchViewModal";

import { getResearchRewards, getScientificResearchDetail } from "../../../reducer/researchReward/action";

const useStyles = makeStyles({
  statusChip: {
    fontSize: 12,
    height: 22,
  },
});

const STATUS_LABEL = {
  not_approved: { label: "Chưa duyệt", color: "default", style: { backgroundColor: "#ff9800", color: "#fff" } },
  pending: { label: "Chờ nhận", color: "default" },
  received: { label: "Đã nhận", color: "primary" },
  withdrawn: { label: "Đã rút", color: "secondary" },
};

const STATUS_OPTIONS = [
  { value: "", label: "Tất cả" },
  { value: "not_approved", label: "Chưa duyệt" },
  { value: "pending", label: "Chờ nhận" },
  { value: "received", label: "Đã nhận" },
  { value: "withdrawn", label: "Đã rút" },
];

function RewardWorks(props) {
  const classes = useStyles();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("");

  const [workItemDetail, setWorkItemDetail] = useState(null);
  const [workItemDetailFull, setWorkItemDetailFull] = useState(null);
  const [isFetchingDetail, setIsFetchingDetail] = useState(false);

  const profileId = props.profile?.id;

  const loadRewards = () => {
    if (!profileId) return;
    props.getResearchRewards({
      profileId,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      status: status || undefined,
    });
  };

  useEffect(() => {
    loadRewards();
  }, [profileId, startDate, endDate, status]);

  const handleOpenWorkDetail = async (row) => {
    const researchId = row.workId || row.id;
    setWorkItemDetail(row);
    setWorkItemDetailFull(null);
    setIsFetchingDetail(true);
    const data = await props.getScientificResearchDetail(researchId);
    setWorkItemDetailFull(data);
    setIsFetchingDetail(false);
  };

  const handleCloseWorkDetail = () => {
    setWorkItemDetail(null);
    setWorkItemDetailFull(null);
    setIsFetchingDetail(false);
  };

  // Build flat work list from the current user's rewards
  let workData = [];
  let totalReward = 0;
  if (props.researchRewards && Array.isArray(props.researchRewards)) {
    let stt = 1;
    props.researchRewards.forEach((item) => {
      totalReward += item.totalReward || 0;
      (item.works || []).forEach((work) => {
        workData.push({
          ...work,
          _stt: stt++,
        });
      });
    });
  }

  const columns = [
    {
      title: <b>STT</b>,
      key: "_stt",
      width: "5%",
      render: (value) => value,
    },
    {
      title: <b>Tên bài / Công trình</b>,
      key: "title",
      width: "47%",
      searchable: true,
      render: (value) => value || "—",
    },
    {
      title: <b>Tiền thưởng</b>,
      key: "rewardAmount",
      width: "18%",
      sorter: (a, b) => (a.rewardAmount || 0) - (b.rewardAmount || 0),
      render: (value) => (
        <span style={{ color: "#1976d2" }}>
          {(value || 0).toLocaleString("vi-VN", { style: "currency", currency: "VND" })}
        </span>
      ),
    },
    {
      title: <b>Trạng thái</b>,
      key: "status",
      width: "20%",
      filters: STATUS_OPTIONS.filter((s) => s.value).map((s) => ({ text: s.label, value: s.value })),
      onFilter: (value, rows) => rows.filter((r) => r.status === value),
      render: (value) => {
        const info = STATUS_LABEL[value] || { label: value, color: "default" };
        return (
          <Chip
            label={info.label}
            color={info.color}
            size="small"
            className={classes.statusChip}
            style={info.style || {}}
          />
        );
      },
    },
    {
      title: <b>Xem</b>,
      key: "_action",
      width: "10%",
      render: (_, row) => (
        <Tooltip title="Xem chi tiết công trình">
          <IconButton size="small" color="primary" onClick={() => handleOpenWorkDetail(row)}>
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      {/* Filter toolbar */}
      <Box
        display="flex"
        alignItems="center"
        style={{ padding: "8px 12px", borderBottom: "1px solid #e0e0e0", marginBottom: 4 }}
      >
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <TextField
              label="Từ ngày"
              type="date"
              size="small"
              variant="outlined"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              style={{ width: 160 }}
            />
          </Grid>
          <Grid item>
            <TextField
              label="Đến ngày"
              type="date"
              size="small"
              variant="outlined"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              style={{ width: 160 }}
            />
          </Grid>
          <Grid item>
            <FormControl size="small" variant="outlined" style={{ minWidth: 150 }}>
              <InputLabel>Trạng thái</InputLabel>
              <Select label="Trạng thái" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Work table */}
      <KPTable
        shadow="none"
        loading={props.isFetching}
        columns={columns}
        rows={workData}
        rowsPerPage={10}
        maxHeight="calc(100vh - 340px)"
      />

      {/* Total reward summary */}
      {workData.length > 0 && (
        <Box style={{ padding: "6px 16px", borderTop: "1px solid #eee", background: "#fafafa" }}>
          <Typography variant="body2">
            <b>Tổng tiền thưởng: </b>
            <span style={{ color: "#1976d2", fontWeight: "bold" }}>
              {totalReward.toLocaleString("vi-VN", { style: "currency", currency: "VND" })}
            </span>
          </Typography>
        </Box>
      )}

      {/* Detail modal */}
      <ScientificResearchViewModal
        open={!!workItemDetail}
        onClose={handleCloseWorkDetail}
        data={workItemDetailFull}
        isFetching={isFetchingDetail}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  isFetching: select(state, "researchRewardReducer", "isFetching"),
  researchRewards: select(state, "researchRewardReducer", "researchRewards"),
  profile: select(state, "profileReducer", "detail"),
});

const mapDispatchToProps = (dispatch) => ({
  getResearchRewards: (payload) => dispatch(getResearchRewards(payload)),
  getScientificResearchDetail: (id) => dispatch(getScientificResearchDetail(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(toJs(RewardWorks));
